import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronDown, ArrowRight, Sun, Flame, Cog, BarChart, Wrench, Phone } from 'lucide-react';
import { Helmet } from 'react-helmet-async';
import img from '../Images/powergeneration.jpg';
import img1 from "../Images/solar.jpg"
import img2 from "../Images/thermalpp.jpg"
import img3 from "../Images/mech.jpg";
import img4 from "../Images/electric.jpg";
import img5 from "../Images/aux.jpg";

const PowerGeneration = () => {
  const [expandedSection, setExpandedSection] = useState(null);
  const [hoveredCard, setHoveredCard] = useState(null);

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: { staggerChildren: 0.1 }
    }
  };

  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: {
        type: "spring",
        stiffness: 100
      }
    }
  };

  const powerPlants = {
    solar: {
      title: "Solar Power Plant",
      icon: <Sun className="w-6 h-6 sm:w-8 sm:h-8" />,
      description: "State-of-the-art solar power generation facilities utilizing photovoltaic technology and concentrated solar power systems for renewable energy production.",
      image: img1,
      features: [
        "High-efficiency photovoltaic panels with automated tracking",
        "Advanced inverter and power conditioning systems",
        "Smart grid integration with real-time monitoring",
        "Battery storage integration capabilities",
        "Scalable design from 1MW to 500MW capacity",
        "Automated cleaning and maintenance systems"
      ],
      specifications: [
        "Panel efficiency: >21%",
        "Tracking system: Dual-axis",
        "Inverter efficiency: >98%",
        "Grid compliance: IEEE 1547",
        "Design life: 25+ years",
        "Remote monitoring: 24/7"
      ],
      applications: [
        "Utility-scale power generation",
        "Industrial power supply",
        "Commercial buildings",
        "Agricultural operations",
        "Microgrids and hybrid systems"
      ]
    },
    thermal: {
      title: "Thermal Power Plant",
      icon: <Flame className="w-6 h-6 sm:w-8 sm:h-8" />,
      description: "Advanced thermal power generation systems utilizing various fuel sources with state-of-the-art combustion and steam cycle technologies.",
      image: img2,
      features: [
        "Supercritical boiler technology",
        "Advanced steam turbine and generator systems",
        "Flue gas desulfurization",
        "Selective catalytic reduction",
        "Advanced combustion controls",
        "Heat recovery steam generators"
      ],
      specifications: [
        "Steam parameters: 600°C, 270 bar",
        "Turbine efficiency: >45%",
        "NOx emissions: <100 mg/Nm³",
        "SOx removal: >95%",
        "Plant availability: >95%",
        "Black start capability"
      ],
      applications: [
        "Base load power generation",
        "Peak load management",
        "District heating systems",
        "Industrial steam supply",
        "Combined cycle operations"
      ]
    }
  };

  const balanceOfPlant = {
    mechanical: {
      title: "Mechanical Systems",
      icon: <Cog className="w-6 h-6 sm:w-8 sm:h-8" />,
      description: "Comprehensive mechanical infrastructure ensuring reliable and efficient plant operations.",
      image: img3,
      components: [
        "Cooling towers and heat exchangers",
        "Fuel handling and preparation systems",
        "High-pressure piping networks",
        "Boiler feed water systems",
        "Compressed air systems",
        "HVAC and ventilation"
      ]
    },
    electrical: {
      title: "Electrical Systems",
      icon: <BarChart className="w-6 h-6 sm:w-8 sm:h-8" />,
      description: "Advanced electrical distribution and control systems for seamless power generation and transmission.",
      image: img4,
      components: [
        "High voltage switchgear",
        "Power transformers and substations",
        "Emergency power systems",
        "Grid synchronization systems",
        "Protection and control systems",
        "Power quality management"
      ]
    },
    auxiliary: {
      title: "Auxiliary Systems",
      icon: <Wrench className="w-6 h-6 sm:w-8 sm:h-8" />,
      description: "Essential support systems and facilities ensuring continuous plant operation.",
      image: img5,
      components: [
        "Water treatment plants",
        "Chemical dosing systems",
        "Fire protection systems",
        "Workshop and maintenance facilities",
        "Environmental monitoring systems",
        "Waste handling facilities"
      ]
    }
  };

  // Generate structured data for SEO
  const structuredData = {
    "@context": "https://schema.org",
    "@type": "ItemList",
    "name": "Power Generation Solutions",
    "description": "Comprehensive power generation systems including solar and thermal power plants with complete balance of plant solutions",
    "itemListElement": [
      ...Object.entries(powerPlants).map(([key, plant], index) => ({
        "@type": "Product",
        "position": index + 1,
        "name": plant.title,
        "description": plant.description,
        "image": plant.image,
        "offers": {
          "@type": "Offer",
          "availability": "https://schema.org/InStock",
          "seller": {
            "@type": "Organization",
            "name": "Gortherm Energy"
          }
        }
      })),
      ...Object.entries(balanceOfPlant).map(([key, system], index) => ({
        "@type": "Product",
        "position": Object.keys(powerPlants).length + index + 1,
        "name": system.title,
        "description": system.description,
        "image": system.image
      }))
    ]
  };

  return (
    <>
      <Helmet>
        <title>Power Generation Solutions | Solar & Thermal Power Plants | Gortherm Energy</title>
        <meta name="description" content="Comprehensive power generation solutions including solar power plants, thermal power plants, and complete balance of plant systems. Advanced renewable and conventional energy technologies for reliable power production." />
        <meta name="keywords" content="power generation, solar power plant, thermal power plant, renewable energy, photovoltaic systems, steam turbines, power plant equipment, balance of plant, electrical systems, mechanical systems, auxiliary systems, energy solutions Nepal" />
        
        {/* Open Graph */}
        <meta property="og:title" content="Power Generation Solutions | Solar & Thermal Power Plants | Gortherm Energy" />
        <meta property="og:description" content="Advanced power generation technologies for sustainable and reliable energy production. Complete turnkey solutions from design to commissioning." />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://www.gorthermenergy.com/power-generation" />
        <meta property="og:image" content={img} />
        
        {/* Twitter Card */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Power Generation Solutions | Gortherm Energy" />
        <meta name="twitter:description" content="Solar and thermal power plants with complete balance of plant systems for reliable energy production." />
        <meta name="twitter:image" content={img} />
        
        {/* Additional SEO */}
        <link rel="canonical" href="https://www.gorthermenergy.com/power-generation" />
        <meta name="robots" content="index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1" />
        <meta name="author" content="Gortherm Energy" />
        <meta name="language" content="English" />
        <meta name="geo.region" content="NP" />
        <meta name="geo.placename" content="Nepal" />
        
        {/* Structured Data */}
        <script type="application/ld+json">
          {JSON.stringify(structuredData)}
        </script>
      </Helmet>

      <main className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100">
        {/* Hero Section */}
        <motion.header
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 1 }}
          className="relative h-[300px] sm:h-[400px] lg:h-[500px] overflow-hidden"
        >
          <img
            src={img}
            alt="Advanced power generation solutions - solar and thermal power plants by Gortherm Energy"
            className="w-full h-full object-cover"
            loading="eager"
            width="1920"
            height="500"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-black/70 to-black/40">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-full flex items-center">
              <motion.div
                initial={{ x: -100, opacity: 0 }}
                animate={{ x: 0, opacity: 1 }}
                transition={{ duration: 0.8, delay: 0.2 }}
                className="max-w-2xl"
              >
                <h1 className="text-3xl sm:text-5xl lg:text-7xl font-bold text-white tracking-tight mb-4 sm:mb-6 leading-tight">
                  Power Generation
                  <span className="text-orange-400"> Solutions</span>
                </h1>
                <p className="text-base sm:text-lg lg:text-xl text-gray-200 mb-6 sm:mb-8">
                  Advanced power generation technologies for sustainable and reliable energy production
                </p>
              </motion.div>
            </div>
          </div>
        </motion.header>

        {/* Power Plants Section */}
        <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 sm:py-16">
          <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-8 sm:mb-12">
            Power Plants
          </h2>
          <motion.div
            variants={containerVariants}
            initial="hidden"
            animate="visible"
            className="grid grid-cols-1 md:grid-cols-2 gap-6 sm:gap-8"
          >
            {Object.entries(powerPlants).map(([key, plant], idx) => (
              <motion.article
                key={key}
                variants={itemVariants}
                whileHover={{ scale: 1.02 }}
                onHoverStart={() => setHoveredCard(idx)}
                onHoverEnd={() => setHoveredCard(null)}
                className="bg-white rounded-xl shadow-lg overflow-hidden"
                itemScope
                itemType="https://schema.org/Product"
              >
                <div className="relative h-48 sm:h-56 md:h-64">
                  <img
                    src={plant.image}
                    alt={`${plant.title} - Advanced power generation technology`}
                    className="w-full h-full object-cover"
                    loading="lazy"
                    width="600"
                    height="400"
                    itemProp="image"
                  />
                </div>
                <div className="p-4 sm:p-6">
                  <div className="flex items-center space-x-3 mb-4">
                    <div className="text-orange-600" aria-hidden="true">
                      {plant.icon}
                    </div>
                    <h3 className="text-lg sm:text-xl font-semibold text-gray-900" itemProp="name">
                      {plant.title}
                    </h3>
                  </div>
                  <p className="text-sm sm:text-base text-gray-600 mb-4" itemProp="description">
                    {plant.description}
                  </p>
                  <button
                    onClick={() => setExpandedSection(expandedSection === idx ? null : idx)}
                    className="flex items-center space-x-2 text-orange-600 hover:text-orange-700 transition-colors text-sm sm:text-base font-medium"
                    aria-expanded={expandedSection === idx}
                    aria-controls={`plant-details-${idx}`}
                  >
                    <span>Learn more</span>
                    <motion.div
                      animate={{ rotate: expandedSection === idx ? 180 : 0 }}
                      transition={{ duration: 0.3 }}
                    >
                      <ChevronDown className="w-4 h-4 sm:w-5 sm:h-5" />
                    </motion.div>
                  </button>

                  <AnimatePresence>
                    {expandedSection === idx && (
                      <motion.div
                        id={`plant-details-${idx}`}
                        initial={{ opacity: 0, height: 0 }}
                        animate={{ opacity: 1, height: "auto" }}
                        exit={{ opacity: 0, height: 0 }}
                        className="mt-4 space-y-4"
                      >
                        <div>
                          <h4 className="font-semibold text-gray-900 mb-2 text-sm sm:text-base">
                            Key Features
                          </h4>
                          <ul className="list-disc pl-5 space-y-1 text-xs sm:text-sm text-gray-600">
                            {plant.features.map((feature, i) => (
                              <li key={i}>{feature}</li>
                            ))}
                          </ul>
                        </div>
                        <div>
                          <h4 className="font-semibold text-gray-900 mb-2 text-sm sm:text-base">
                            Technical Specifications
                          </h4>
                          <ul className="list-disc pl-5 space-y-1 text-xs sm:text-sm text-gray-600">
                            {plant.specifications.map((spec, i) => (
                              <li key={i}>{spec}</li>
                            ))}
                          </ul>
                        </div>
                        <div>
                          <h4 className="font-semibold text-gray-900 mb-2 text-sm sm:text-base">
                            Applications
                          </h4>
                          <ul className="list-disc pl-5 space-y-1 text-xs sm:text-sm text-gray-600">
                            {plant.applications.map((app, i) => (
                              <li key={i}>{app}</li>
                            ))}
                          </ul>
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              </motion.article>
            ))}
          </motion.div>
        </section>

        {/* Balance of Plant Section */}
        <section className="bg-gray-50 py-12 sm:py-16">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-8 sm:mb-12">
              Balance of Plant
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 sm:gap-8">
              {Object.entries(balanceOfPlant).map(([key, system]) => (
                <motion.article
                  key={key}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  className="bg-white rounded-xl overflow-hidden shadow-xl"
                  itemScope
                  itemType="https://schema.org/Product"
                >
                  <div className="relative h-40 sm:h-48">
                    <img
                      src={system.image}
                      alt={`${system.title} - Power plant infrastructure and support systems`}
                      className="w-full h-full object-cover"
                      loading="lazy"
                      width="400"
                      height="300"
                      itemProp="image"
                    />
                  </div>
                  <div className="p-6 sm:p-8">
                    <div className="flex items-center space-x-3 mb-4">
                      <div className="text-blue-600" aria-hidden="true">
                        {system.icon}
                      </div>
                      <h3 className="text-xl sm:text-2xl font-bold text-gray-900" itemProp="name">
                        {system.title}
                      </h3>
                    </div>
                    <p className="text-sm sm:text-base text-gray-600 mb-6" itemProp="description">
                      {system.description}
                    </p>
                    <div className="bg-gray-50 rounded-lg p-4 sm:p-6">
                      <h4 className="text-base sm:text-lg font-semibold text-gray-900 mb-4">
                        Key Components
                      </h4>
                      <ul className="space-y-2 sm:space-y-3">
                        {system.components.map((component, idx) => (
                          <li key={idx} className="flex items-start space-x-2 text-xs sm:text-sm text-gray-700">
                            <ArrowRight className="w-4 h-4 text-blue-600 flex-shrink-0 mt-0.5" aria-hidden="true" />
                            <span>{component}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                </motion.article>
              ))}
            </div>
          </div>
        </section>

        {/* Why Choose Section */}
        <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 sm:py-16">
          <div className="bg-white rounded-2xl p-6 sm:p-8 lg:p-12 shadow-lg">
            <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-6 sm:mb-8">
              Why Choose Our Power Generation Solutions?
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 sm:gap-8 text-gray-700">
              <div>
                <h3 className="text-lg sm:text-xl font-semibold mb-2 text-orange-600">
                  Turnkey Solutions
                </h3>
                <p className="text-sm sm:text-base">
                  Complete end-to-end power plant solutions from initial design and engineering 
                  to construction, commissioning, and ongoing maintenance support.
                </p>
              </div>
              <div>
                <h3 className="text-lg sm:text-xl font-semibold mb-2 text-orange-600">
                  Advanced Technology
                </h3>
                <p className="text-sm sm:text-base">
                  State-of-the-art power generation technologies featuring high efficiency, 
                  low emissions, and reliable operation for both renewable and conventional systems.
                </p>
              </div>
              <div>
                <h3 className="text-lg sm:text-xl font-semibold mb-2 text-orange-600">
                  Environmental Compliance
                </h3>
                <p className="text-sm sm:text-base">
                  Environmentally responsible designs meeting international emission standards 
                  with advanced pollution control and waste management systems.
                </p>
              </div>
              <div>
                <h3 className="text-lg sm:text-xl font-semibold mb-2 text-orange-600">
                  Operational Excellence
                </h3>
                <p className="text-sm sm:text-base">
                  High plant availability, efficient performance, and comprehensive monitoring 
                  systems ensuring maximum uptime and optimal energy production.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <aside className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-12 lg:py-16">
          <div className="bg-gradient-to-r from-orange-50 to-yellow-50 rounded-2xl p-6 sm:p-8 lg:p-12 shadow-lg text-center">
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-gray-900 mb-4">
              Ready to Power Your Future?
            </h2>
            <p className="text-base sm:text-lg text-gray-700 mb-6 sm:mb-8 max-w-2xl mx-auto">
              Our power generation experts are ready to help you design and implement 
              the perfect energy solution for your organization.
            </p>
            <a 
              href="tel:+9779802781400"
              className="inline-block"
              aria-label="Contact power generation experts at +977 980 278 1400"
            >
              <button className="bg-orange-600 hover:bg-orange-700 active:bg-orange-800 text-white px-6 sm:px-8 lg:px-10 py-3 sm:py-4 rounded-lg flex items-center gap-2 sm:gap-3 mx-auto transition-all duration-300 shadow-md hover:shadow-xl text-sm sm:text-base lg:text-lg font-semibold">
                <Phone className="h-5 w-5 sm:h-6 sm:w-6" aria-hidden="true" />
                <span>Contact Our Experts</span>
              </button>
            </a>
            
          </div>
        </aside>
      </main>
    </>
  );
};

export default PowerGeneration;